/**
 * Blog Hub JavaScript
 * Handles search, filtering, and dynamic interactions for the blog landing page
 */

class BlogHub {
  constructor() {
    // Elements
    this.searchInput = document.querySelector('[data-search-input]');
    this.searchSuggestions = document.querySelector('[data-search-suggestions]');
    this.categoryPills = document.querySelectorAll('.category-pill');
    this.articlesGrid = document.querySelector('[data-articles-grid]');
    this.articleCards = this.articlesGrid ? [...this.articlesGrid.querySelectorAll('.blog-card')] : [];
    this.emptyState = document.querySelector('[data-empty-state]');
    this.clearFiltersButtons = document.querySelectorAll('[data-clear-filters]');
    this.visibleCountElement = document.querySelector('[data-visible-count]');
    this.totalCountElement = document.querySelector('[data-total-count]');
    this.loadMoreButton = document.querySelector('[data-load-more]');
    this.viewAllLink = document.querySelector('[data-view-all-link]');
    this.viewAllCategory = document.querySelector('[data-view-all-category]');

    // Pagination state
    this.ARTICLES_PER_PAGE = 12;
    this.currentPage = 1;
    this.filteredArticles = [];

    // State
    this.activeCategory = 'all';
    this.searchQuery = '';
    this.searchTimeout = null;

    // Store category counts from pills (these show the TRUE totals)
    this.categoryTotals = {};
    this.categoryTitles = {};
    this.categoryPills.forEach((pill) => {
      const category = pill.dataset.category;
      const text = pill.textContent.trim();
      // Extract count from "Tips (74)" format
      const match = text.match(/^(.+?)\s*\((\d+)\)$/);
      if (match) {
        this.categoryTitles[category] = match[1].trim();
        this.categoryTotals[category] = parseInt(match[2], 10);
      }
    });

    // Initialize
    this.init();
  }

  init() {
    if (!this.articlesGrid) return;

    // Debug: log all blog handles found on cards
    const blogHandles = this.articleCards.map(card => card.dataset.blogHandle);
    const uniqueHandles = [...new Set(blogHandles)];
    console.log('BlogHub init:', {
      gridFound: !!this.articlesGrid,
      cardsFound: this.articleCards.length,
      uniqueBlogHandles: uniqueHandles,
      handleCounts: uniqueHandles.reduce((acc, h) => {
        acc[h] = blogHandles.filter(bh => bh === h).length;
        return acc;
      }, {})
    });

    // Bind event listeners
    this.bindEvents();

    // Initialize from URL params
    this.initializeFromURL();

    // Initial filter
    this.filterArticles();
  }

  bindEvents() {
    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e));
      this.searchInput.addEventListener('focus', () => this.showSearchSuggestions());
      this.searchInput.addEventListener('blur', () => {
        // Delay to allow click events on suggestions
        setTimeout(() => this.hideSearchSuggestions(), 200);
      });
    }

    // Category pills
    this.categoryPills.forEach((pill) => {
      pill.addEventListener('click', (e) => this.handleCategoryClick(e));
    });

    // Clear filters buttons
    this.clearFiltersButtons.forEach((button) => {
      button.addEventListener('click', () => this.clearAllFilters());
    });

    // Load more button
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', () => this.handleLoadMore());
    }

    // Handle browser back/forward
    window.addEventListener('popstate', () => this.initializeFromURL());
  }

  handleSearch(e) {
    const query = e.target.value.toLowerCase().trim();

    // Debounce search
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.searchQuery = query;
      this.currentPage = 1; // Reset pagination on search
      this.filterArticles();
      this.updateURL();
    }, 300);
  }

  handleCategoryClick(e) {
    const button = e.currentTarget;
    const category = button.dataset.category;

    // Update active state
    this.categoryPills.forEach((pill) => pill.classList.remove('active'));
    button.classList.add('active');

    // Update category and filter
    this.activeCategory = category;
    this.currentPage = 1; // Reset pagination on category change
    this.filterArticles();
    this.updateURL();
  }

  filterArticles() {
    // Debug: log card count and active category
    console.log('BlogHub filterArticles:', {
      totalCards: this.articleCards.length,
      activeCategory: this.activeCategory,
      searchQuery: this.searchQuery
    });

    // Filter articles based on category and search
    this.filteredArticles = this.articleCards.filter((card) => {
      const matchesCategory = this.matchesCategory(card);
      const matchesSearch = this.matchesSearch(card);
      return matchesCategory && matchesSearch;
    });

    console.log('Filtered results:', this.filteredArticles.length);

    // Apply pagination
    const articlesToShow = this.currentPage * this.ARTICLES_PER_PAGE;
    let visibleCount = 0;

    this.articleCards.forEach((card) => {
      const isInFilteredSet = this.filteredArticles.includes(card);
      const isWithinPageLimit = this.filteredArticles.indexOf(card) < articlesToShow;
      const isVisible = isInFilteredSet && isWithinPageLimit;

      card.hidden = !isVisible;

      if (isVisible) {
        visibleCount++;
        // Add staggered animation
        card.style.setProperty('--animation-order', visibleCount);
      }
    });

    // Update count to show total filtered (not just visible due to pagination)
    this.updateVisibleCount(this.filteredArticles.length);

    // Show/hide empty state
    this.toggleEmptyState(this.filteredArticles.length === 0);

    // Show/hide clear filters button
    this.toggleClearFilters();

    // Show/hide load more button
    this.toggleLoadMore();

    // Show/hide "View all" link if there are more articles than loaded
    this.toggleViewAllLink();
  }

  toggleViewAllLink() {
    if (!this.viewAllLink) return;

    // Only show for specific category filters (not "all")
    if (this.activeCategory === 'all') {
      this.viewAllLink.hidden = true;
      return;
    }

    const totalInCategory = this.categoryTotals[this.activeCategory] || 0;
    const loadedInCategory = this.filteredArticles.length;
    const hasMore = totalInCategory > loadedInCategory;

    if (hasMore) {
      this.viewAllLink.hidden = false;
      this.viewAllLink.href = `/blogs/${this.activeCategory}`;
      if (this.viewAllCategory) {
        this.viewAllCategory.textContent = this.categoryTitles[this.activeCategory] || this.activeCategory;
      }
    } else {
      this.viewAllLink.hidden = true;
    }
  }

  matchesCategory(card) {
    if (this.activeCategory === 'all') return true;
    return card.dataset.blogHandle === this.activeCategory;
  }

  matchesSearch(card) {
    if (!this.searchQuery) return true;

    const title = card.dataset.title?.toLowerCase() || '';
    const excerpt = card.dataset.excerpt?.toLowerCase() || '';
    const author = card.dataset.author?.toLowerCase() || '';

    return (
      title.includes(this.searchQuery) ||
      excerpt.includes(this.searchQuery) ||
      author.includes(this.searchQuery)
    );
  }

  updateVisibleCount(count) {
    if (this.visibleCountElement) {
      this.visibleCountElement.textContent = count;
    }
  }

  toggleEmptyState(show) {
    if (this.emptyState) {
      this.emptyState.hidden = !show;
    }
    if (this.articlesGrid) {
      this.articlesGrid.style.display = show ? 'none' : '';
    }
  }

  toggleClearFilters() {
    const hasFilters = this.activeCategory !== 'all' || this.searchQuery !== '';
    this.clearFiltersButtons.forEach((button) => {
      button.hidden = !hasFilters;
    });
  }

  clearAllFilters() {
    // Reset search
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.searchQuery = '';

    // Reset category to "All"
    this.activeCategory = 'all';
    this.categoryPills.forEach((pill) => {
      pill.classList.toggle('active', pill.dataset.category === 'all');
    });

    // Reset pagination
    this.currentPage = 1;

    // Refilter and update URL
    this.filterArticles();
    this.updateURL();
  }

  handleLoadMore() {
    this.currentPage++;
    this.filterArticles();

    // Smooth scroll to the first newly revealed article
    setTimeout(() => {
      const previousCount = (this.currentPage - 1) * this.ARTICLES_PER_PAGE;
      const newlyRevealedCard = this.filteredArticles[previousCount];
      if (newlyRevealedCard) {
        newlyRevealedCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  }

  toggleLoadMore() {
    if (!this.loadMoreButton) return;

    const totalFiltered = this.filteredArticles.length;
    const currentlyShowing = this.currentPage * this.ARTICLES_PER_PAGE;
    const hasMore = currentlyShowing < totalFiltered;

    if (hasMore) {
      this.loadMoreButton.parentElement.hidden = false;
      const remaining = totalFiltered - currentlyShowing;
      const toLoad = Math.min(remaining, this.ARTICLES_PER_PAGE);
      this.loadMoreButton.textContent = `Load ${toLoad} more article${toLoad !== 1 ? 's' : ''}`;
    } else {
      this.loadMoreButton.parentElement.hidden = true;
    }
  }

  updateURL() {
    const params = new URLSearchParams();

    if (this.activeCategory !== 'all') {
      params.set('category', this.activeCategory);
    }

    if (this.searchQuery) {
      params.set('q', this.searchQuery);
    }

    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    // Update URL without reload
    window.history.replaceState({}, '', newURL);
  }

  initializeFromURL() {
    const params = new URLSearchParams(window.location.search);

    // Set category from URL
    const category = params.get('category');
    if (category) {
      this.activeCategory = category;
      this.categoryPills.forEach((pill) => {
        pill.classList.toggle('active', pill.dataset.category === category);
      });
    }

    // Set search from URL
    const query = params.get('q');
    if (query && this.searchInput) {
      this.searchInput.value = query;
      this.searchQuery = query.toLowerCase().trim();
    }

    // Apply filters
    this.filterArticles();
  }

  showSearchSuggestions() {
    if (this.searchSuggestions && this.searchQuery) {
      // Future: populate with actual suggestions
      this.searchSuggestions.hidden = false;
    }
  }

  hideSearchSuggestions() {
    if (this.searchSuggestions) {
      this.searchSuggestions.hidden = true;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BlogHub();
  });
} else {
  new BlogHub();
}
